import { Box, Button, InputAdornment } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { TextField } from 'mui-rff';
import { Form, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';

export type SocialBenefitEmployeeConfigFormValues = {
  sbFoodAccrual: string;
  sbTransportAccrual: string;
};

type SocialBenefitEmployeeConfigFormContentProps = {
  currencySymbol: string;
  submit: () => void;
  isSaving: boolean;
  isReadOnly: boolean;
};

const SocialBenefitEmployeeConfigFormContent = ({
  currencySymbol,
  submit,
  isSaving,
  isReadOnly
}: SocialBenefitEmployeeConfigFormContentProps) => {
  const { t } = useTranslation(['flexflow', 'common']);
  const { pristine, valid } = useFormState<SocialBenefitEmployeeConfigFormValues>();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <TextField
          label={t('socialBenefitEmployeeDetail.configuration.food', { ns: 'flexflow' })}
          name="sbFoodAccrual"
          type="number"
          disabled={isReadOnly}
          sx={{ flex: 1 }}
          inputProps={{ min: 0, step: '0.01' }}
          InputProps={{
            startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>
          }}
          placeholder="-"
        />
        <TextField
          label={t('socialBenefitEmployeeDetail.configuration.transport', { ns: 'flexflow' })}
          name="sbTransportAccrual"
          type="number"
          disabled={isReadOnly}
          sx={{ flex: 1 }}
          inputProps={{ min: 0, step: '0.01' }}
          InputProps={{
            startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>
          }}
          placeholder="-"
        />
      </Box>
      {!isReadOnly && (
        <Grid2 xs={12} sx={{ mt: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={submit} disabled={pristine || !valid || isSaving}>
            {t('common:save')}
          </Button>
        </Grid2>
      )}
    </Box>
  );
};

type SocialBenefitEmployeeConfigFormProps = {
  currencySymbol: string;
  sbFoodAccrual: number;
  sbTransportAccrual: number;
  isSaving: boolean;
  isReadOnly: boolean;
  onSubmit: (values: SocialBenefitEmployeeConfigFormValues) => Promise<void>;
};

const validate = (values: SocialBenefitEmployeeConfigFormValues) => {
  const errors: Partial<Record<keyof SocialBenefitEmployeeConfigFormValues, string>> = {};
  const parsedFood = Number(values.sbFoodAccrual);
  const parsedTransport = Number(values.sbTransportAccrual);

  if (!Number.isFinite(parsedFood) || parsedFood < 0) {
    errors.sbFoodAccrual = ' ';
  }
  if (!Number.isFinite(parsedTransport) || parsedTransport < 0) {
    errors.sbTransportAccrual = ' ';
  }

  return errors;
};

const SocialBenefitEmployeeConfigForm = ({
  currencySymbol,
  sbFoodAccrual,
  sbTransportAccrual,
  isSaving,
  isReadOnly,
  onSubmit
}: SocialBenefitEmployeeConfigFormProps) => {
  return (
    <Form<SocialBenefitEmployeeConfigFormValues>
      onSubmit={onSubmit}
      initialValues={{
        sbFoodAccrual: String(sbFoodAccrual),
        sbTransportAccrual: String(sbTransportAccrual)
      }}
      validate={validate}
      render={({ form: { submit } }) => (
        <SocialBenefitEmployeeConfigFormContent
          currencySymbol={currencySymbol}
          submit={submit}
          isSaving={isSaving}
          isReadOnly={isReadOnly}
        />
      )}
    />
  );
};

export default SocialBenefitEmployeeConfigForm;
